import {
	ReactElement,
	Ref,
	useRef,
	useState
} from "react";
import { DropOverActionList } from "../DropOverActionList/DropOverActionList";
import styles from "./InlineMultiOptionSelector.module.less";
import { generateTrackingId } from "client/Misc/utils";
import { ActionListCheckbox } from "../ActionList/ActionListCheckbox";
import { InlineOptionSelectorPreview } from "../InlineOptionSelector/InlineOptionSelectorPreview";
import { Space } from "../Space/Space";

export type InlineMultiOptionSelectorItem<TValue> = {
	text: string;
	value: TValue;
};

type DropOverAlignment = "left" | "right";

export type InlineMultiOptionSelectorProps<TValue> = {
	value: readonly TValue[];
	onChange(value: readonly TValue[]): void;
	options: readonly InlineMultiOptionSelectorItem<TValue>[];
	text: string;
	dropOverAlignment?: DropOverAlignment;
	trackingId?: string;
	menuTitle: string;
};

export const InlineMultiOptionSelector = <TValue,>(
	props: InlineMultiOptionSelectorProps<TValue>
): ReactElement => {
	const {
		value,
		onChange,
		options,
		text,
		dropOverAlignment = "left",
		trackingId,
		menuTitle
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const ref: Ref<HTMLButtonElement> = useRef<HTMLButtonElement>(null);

	const changeHandler = (item: InlineMultiOptionSelectorItem<TValue>, becameChecked: boolean): void => {
		const copy: TValue[] = [...value];
		if (becameChecked) {
			copy.push(item.value);
		} else {
			const indexInValue: number = value.indexOf(item.value);
			copy.splice(indexInValue, 1);
		}
		onChange(copy);
	};

	return (
		<div
			className={styles.root}
		>
			<InlineOptionSelectorPreview
				ref={ref}
				onClick={() => setIsOpen(isOpen => !isOpen)}
				trackingId={trackingId}
				isLeftAligned={dropOverAlignment === "left"}
			>
				{ text }
				{value.length > 0 && (
					<>
						<Space />
						({value.length})
					</>
				)}
			</InlineOptionSelectorPreview>
			{isOpen && ref.current && (
				<DropOverActionList
					target={ref.current}
					onClose={() => setIsOpen(false)}
					isHeightConstrainted
					trackingId={generateTrackingId(trackingId, "__dropover")}
					targetAlignment={dropOverAlignment === "left" ? "bl" : "br"}
					contentAlignment={dropOverAlignment === "left" ? "tl" : "tr"}
					menuTitle={menuTitle}
				>
					{ options.map((item, index) => (
						<ActionListCheckbox
							key={index}
							text={item.text}
							onChange={(checkedState) => changeHandler(item, checkedState)}
							shouldNotAutoCloseContainingDropOver
							value={value.indexOf(item.value) !== -1}
							trackingId={generateTrackingId(trackingId, `__item|${item.value}`)}
						/>
					))}
				</DropOverActionList>
			)}
		</div>
	);
};
InlineMultiOptionSelector.displayName = "InlineMultiOptionSelector";
